import type { FC } from 'react'
import { Box } from '@mui/material'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronRightIcon, EditSolidIcon } from '@dhl-ui/icons'
import { Body2, ListItem, ListItemText } from '@dhl-ui/components'

import type { LocationStateWithReferrer } from '~/types/locationState.js'
import { links } from '~/links.js'
import { useGetInterventionPreferencesByAddressId } from '~/profile/model/preferences/hooks.js'
import { isFeatureEnabled } from '~/config/index.js'

export type Params = {
  addressId: string
}

type Props = {
  isAddressMutated?: boolean
}

export const AddressFormDeliveryPreference: FC<Props> = ({ isAddressMutated }) => {
  const { t } = useTranslation()

  const location = useLocation<LocationStateWithReferrer>()
  const navigate = useNavigate()
  const { addressId } = useParams<Params>()
  const { data: preference } = useGetInterventionPreferencesByAddressId(addressId)
  const servicePointPreference = preference?.servicePoint
  const agreedPlacePreference = preference?.agreedPlace

  const isNeighbourPreference =
    agreedPlacePreference !== undefined &&
    agreedPlacePreference?.ringDoorbell !== null &&
    !agreedPlacePreference?.active
  const hasActivePreference = servicePointPreference?.active || agreedPlacePreference?.active || isNeighbourPreference

  const handleShippingPreferenceClick = () => {
    const { pathname, state } = location
    const target = isFeatureEnabled('profile.deliveryPreferences.deliveryPreferencesV2')
      ? links.deliveryPreferenceDetailsLink({ addressId })
      : links.preferredSettingsLink({ addressId })

    navigate(target, {
      state: {
        ...state,
        referrer: pathname,
      },
    })
  }

  if (!addressId) {
    return null
  }

  return (
    <ListItem
      isButton
      hasDivider={false}
      alignItems="center"
      data-test="shipping-preferences"
      isDisabled={isAddressMutated}
      slotProps={{
        secondaryIcon: (
          <>
            {!isAddressMutated && hasActivePreference && <EditSolidIcon />}
            {!isAddressMutated && !hasActivePreference && <ChevronRightIcon />}
          </>
        ),
      }}
      onClick={handleShippingPreferenceClick}
    >
      <ListItemText
        emphasis
        data-test="shipping-preferences-text"
        primary={
          hasActivePreference
            ? t('portal.profile.addressForm.editShippingPreferences')
            : t('portal.profile.addressForm.addShippingPreferences')
        }
        secondary={
          <Body2 data-test="active-shipping-preference" translate="no" component="span">
            {!servicePointPreference?.active && !agreedPlacePreference?.active && (
              <Box component="span">
                {preference === undefined || agreedPlacePreference?.ringDoorbell === null
                  ? t('portal.preferences.defaultLocationText')
                  : t('portal.preferences.neighbours.ringDoorbellDescription')}
              </Box>
            )}
            {servicePointPreference?.active && (
              <>
                {t('portal.preferences.servicePoint.titleText')} {servicePointPreference.name}.
              </>
            )}
            {agreedPlacePreference?.active && (
              <>
                {agreedPlacePreference?.ringDoorbell
                  ? t('portal.preferences.agreedPlace.cardTextWithRingDoorbell')
                  : t('portal.preferences.agreedPlace.cardTextWithoutRingDoorbell')}{' '}
                {agreedPlacePreference.description}.
              </>
            )}
          </Body2>
        }
      />
    </ListItem>
  )
}
